// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useResizeObserver, useStableCallback } from '@cloudscape-design/component-toolkit/internal';
import { getLogicalBoundingClientRect } from '@cloudscape-design/component-toolkit/internal';
import { setElementWidths } from './column-widths-utils';
export const DEFAULT_COLUMN_WIDTH = 120;
function readWidths(getCell, visibleColumns) {
    const result = new Map();
    for (let index = 0; index < visibleColumns.length; index++) {
        const column = visibleColumns[index];
        let width = column.width || 0;
        const minWidth = column.minWidth || width || DEFAULT_COLUMN_WIDTH;
        if (!width && // read width from the DOM if it is missing in the config
            index !== visibleColumns.length - 1 // skip reading for the last column, because it expands to fully fit the container
        ) {
            const colEl = getCell(column.id);
            width = colEl ? getLogicalBoundingClientRect(colEl).inlineSize : DEFAULT_COLUMN_WIDTH;
        }
        result.set(column.id, Math.max(width, minWidth));
    }
    return result;
}
function updateWidths(visibleColumns, oldWidths, newWidth, columnId) {
    const column = visibleColumns.find(column => column.id === columnId);
    let minWidth = DEFAULT_COLUMN_WIDTH;
    if (typeof (column === null || column === void 0 ? void 0 : column.width) === 'number' && column.width < DEFAULT_COLUMN_WIDTH) {
        minWidth = column === null || column === void 0 ? void 0 : column.width;
    }
    if (typeof (column === null || column === void 0 ? void 0 : column.minWidth) === 'number') {
        minWidth = column === null || column === void 0 ? void 0 : column.minWidth;
    }
    newWidth = Math.max(newWidth, minWidth);
    if (oldWidths.get(columnId) === newWidth) {
        return oldWidths;
    }
    const newWidths = new Map(oldWidths);
    newWidths.set(columnId, newWidth);
    return newWidths;
}
const WidthsContext = createContext({
    getColumnStyles: () => ({}),
    columnWidths: new Map(),
    updateColumn: () => { },
    updateGroup: () => { },
    setCell: () => { },
    setCol: () => { },
});
export function ColumnWidthsProvider({ visibleColumns, resizableColumns, containerRef, groupColumnMap, children, }) {
    const visibleColumnsRef = useRef(null);
    const containerWidthRef = useRef(0);
    const [columnWidths, setColumnWidths] = useState(null);
    const cellsRef = useRef(new Map());
    const stickyCellsRef = useRef(new Map());
    const colsRef = useRef(new Map());
    const hasColElements = useRef(false);
    const getCell = (columnId) => { var _a; return (_a = cellsRef.current.get(columnId)) !== null && _a !== void 0 ? _a : null; };
    const setCell = (sticky, columnId, node) => {
        const ref = sticky ? stickyCellsRef : cellsRef;
        if (node) {
            ref.current.set(columnId, node);
        }
        else {
            ref.current.delete(columnId);
        }
    };
    const setCol = (columnId, node) => {
        if (node) {
            colsRef.current.set(columnId, node);
            hasColElements.current = true;
        }
        else {
            colsRef.current.delete(columnId);
            hasColElements.current = colsRef.current.size > 0;
        }
    };
    const getColumnStyles = (sticky, columnId) => {
        var _a, _b, _c;
        const column = visibleColumns.find(col => col.id === columnId);
        if (sticky) {
            // For sticky headers, mirror the primary cell's width.
            // Try DOM measurement first (handles columns not in visibleColumns like selection).
            const measured = (_a = cellsRef.current.get(columnId)) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width;
            /* istanbul ignore next: getBoundingClientRect returns 0 in JSDOM */
            if (measured) {
                return { width: measured };
            }
            return { width: (_b = columnWidths === null || columnWidths === void 0 ? void 0 : columnWidths.get(columnId)) !== null && _b !== void 0 ? _b : column === null || column === void 0 ? void 0 : column.width };
        }
        if (!column) {
            return {};
        }
        if (resizableColumns && columnWidths) {
            const isLastColumn = column.id === ((_c = visibleColumns[visibleColumns.length - 1]) === null || _c === void 0 ? void 0 : _c.id);
            const totalWidth = visibleColumns.reduce((sum, { id }) => sum + (columnWidths.get(id) || DEFAULT_COLUMN_WIDTH), 0);
            if (isLastColumn && containerWidthRef.current > totalWidth) {
                return { width: 'auto', minWidth: column.minWidth };
            }
            return { width: columnWidths.get(column.id), minWidth: column.minWidth };
        }
        return {
            width: column.width,
            minWidth: column.minWidth,
            maxWidth: !resizableColumns ? column.maxWidth : undefined,
        };
    };
    // Mirrors the current primary cell widths onto the sticky header copies.
    const syncStickyColumnWidths = useStableCallback(() => {
        for (const { id } of visibleColumns) {
            const element = stickyCellsRef.current.get(id);
            if (element) {
                setElementWidths(element, getColumnStyles(true, id));
            }
        }
    });
    // Imperatively sets width style for a cell avoiding React state.
    // This allows setting the style as soon container's size change is observed.
    const updateColumnWidths = useStableCallback(() => {
        // When col elements exist (grouped columns), apply widths to <col> elements.
        // With table-layout:fixed, <col> widths control the actual column widths.
        if (hasColElements.current) {
            for (const { id } of visibleColumns) {
                const colElement = colsRef.current.get(id);
                if (colElement) {
                    setElementWidths(colElement, getColumnStyles(false, id));
                }
                const element = cellsRef.current.get(id);
                if (element) {
                    setElementWidths(element, getColumnStyles(false, id));
                }
            }
        }
        else {
            for (const { id } of visibleColumns) {
                const element = cellsRef.current.get(id);
                if (element) {
                    setElementWidths(element, getColumnStyles(false, id));
                }
            }
        }
        // Sticky column widths must always be synchronized regardless of columnWidths state.
        syncStickyColumnWidths();
    });
    // Re-syncs the sticky header copies when the primary cells change width without a
    // corresponding change to the column definitions. This happens with auto-layout
    // columns whose widths depend on the rendered content (e.g. paginating or filtering).
    // Fixed-width and resizable columns don't resize on data changes, so this stays idle.
    // The effect re-observes when the set of visible columns changes, since that swaps
    // the underlying cell elements.
    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => syncStickyColumnWidths());
        for (const element of cellsRef.current.values()) {
            observer.observe(element);
        }
        return () => observer.disconnect();
    }, [syncStickyColumnWidths, visibleColumns]);
    // Observes container size and requests an update to the last cell width as it depends on the container's width.
    useResizeObserver(containerRef, ({ contentBoxWidth: containerWidth }) => {
        containerWidthRef.current = containerWidth;
        requestAnimationFrame(() => updateColumnWidths());
    });
    // The widths of the dynamically added columns (after the first render) if not set explicitly
    // will default to the DEFAULT_COLUMN_WIDTH.
    useEffect(() => {
        updateColumnWidths();
        if (!resizableColumns) {
            return;
        }
        let updated = false;
        const newColumnWidths = new Map(columnWidths);
        const lastVisible = visibleColumnsRef.current;
        if (lastVisible) {
            for (let index = 0; index < visibleColumns.length; index++) {
                const column = visibleColumns[index];
                if (!(columnWidths === null || columnWidths === void 0 ? void 0 : columnWidths.get(column.id)) && lastVisible.indexOf(column.id) === -1) {
                    updated = true;
                    const width = column.width || DEFAULT_COLUMN_WIDTH;
                    const minWidth = column.minWidth || width;
                    newColumnWidths.set(column.id, Math.max(width, minWidth));
                }
            }
            if (updated) {
                setColumnWidths(newColumnWidths);
            }
        }
        visibleColumnsRef.current = visibleColumns.map(column => column.id);
    }, [columnWidths, resizableColumns, visibleColumns, updateColumnWidths]);
    // Read the actual column widths after the first render to employ the browser defaults for
    // those columns without explicit width.
    useEffect(() => {
        if (!resizableColumns) {
            return;
        }
        setColumnWidths(() => readWidths(getCell, visibleColumns));
        // This code is intended to run only at the first render and should not re-run when table props change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    function updateColumn(columnId, newWidth) {
        setColumnWidths(columnWidths => updateWidths(visibleColumns, columnWidths !== null && columnWidths !== void 0 ? columnWidths : new Map(), newWidth, columnId));
    }
    function updateGroup(groupId, newGroupWidth) {
        var _a;
        if (!columnWidths || !groupColumnMap) {
            return;
        }
        const columnIds = (_a = groupColumnMap.get(String(groupId))) !== null && _a !== void 0 ? _a : [];
        const rightmostColumn = columnIds[columnIds.length - 1];
        if (!rightmostColumn) {
            return;
        }
        let currentGroupWidth = 0;
        for (const id of columnIds) {
            currentGroupWidth += columnWidths.get(id) || DEFAULT_COLUMN_WIDTH;
        }
        const delta = newGroupWidth - currentGroupWidth;
        const currentColumnWidth = columnWidths.get(rightmostColumn) || DEFAULT_COLUMN_WIDTH;
        updateColumn(rightmostColumn, currentColumnWidth + delta);
    }
    return (React.createElement(WidthsContext.Provider, { value: { getColumnStyles, columnWidths: columnWidths !== null && columnWidths !== void 0 ? columnWidths : new Map(), updateColumn, updateGroup, setCell, setCol } }, children));
}
export function useColumnWidths() {
    return useContext(WidthsContext);
}
//# sourceMappingURL=use-column-widths.js.map